import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, ArrowRight, Truck, Tag, ChevronDown, ChevronUp, AlertCircle, Loader2, X, AlertTriangle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { coupons as couponsApi, type CouponAPI } from '../api/client';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import { freeShippingState, shippingFee } from '../lib/shipping';

export default function CartPage() {
  const { state, totalItems, totalPrice, removeFromCart, updateQuantity, clearCart, appliedCoupon, discountAmount, setAppliedCoupon, removeCoupon: clearCoupon } = useCart();
  const { isDark } = useTheme();

  // Coupon state (appliedCoupon + discountAmount live in CartContext, shared with checkout)
  const [availableCoupons, setAvailableCoupons] = useState<CouponAPI[]>([]);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponLoading, setCouponLoading] = useState<string | null>(null);
  const [showCoupons, setShowCoupons] = useState(false);

  useEffect(() => {
    if (state.items.length > 0) {
      couponsApi.active().then(setAvailableCoupons).catch(() => {});
    }
  }, [state.items.length]);

  // Re-validate applied coupon when cart total changes
  useEffect(() => {
    if (!appliedCoupon || totalPrice === 0) return;
    couponsApi.validate(appliedCoupon.code, totalPrice).then((res) => {
      if (res.valid) {
        setAppliedCoupon(appliedCoupon, res.discount_amount);
      } else {
        clearCoupon();
        setCouponError(res.message);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPrice]);

  const applyCoupon = async (code: string) => {
    setCouponError(null);
    setCouponLoading(code);
    try {
      const res = await couponsApi.validate(code, totalPrice);
      if (res.valid) {
        setAppliedCoupon(res.coupon, res.discount_amount);
        setShowCoupons(false);
      } else {
        setCouponError(res.message);
      }
    } catch {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(null);
    }
  };

  const removeCoupon = () => {
    clearCoupon();
    setCouponError(null);
  };

  if (state.items.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <SEO {...pageSeoMeta.cartEmpty} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <ShoppingBag size={80} className={`mx-auto mb-6 ${isDark ? 'text-gray-700' : 'text-gray-200'}`} />
          <h1 className={`text-2xl font-extrabold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Your Cart is Empty</h1>
          <p className={`mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Looks like you haven't added any products yet. Browse our store to find the perfect nicotine pouch for you.
          </p>
          <Link
            to="/store"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all"
          >
            <ArrowLeft size={18} /> Continue Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  const shipping = shippingFee(totalPrice);
  const { reached: freeShippingReached, remaining: freeShippingRemaining, pct: freeShippingPct } = freeShippingState(totalPrice);
  const grandTotal = totalPrice - discountAmount + shipping;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO {...pageSeoMeta.cart} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/store"
          className={`inline-flex items-center gap-2 mb-8 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <ArrowLeft size={16} /> Continue Shopping
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-xl sm:text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>Shopping Cart</h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{totalItems} {totalItems === 1 ? 'item' : 'items'} in your cart</p>
          </div>
          <button
            onClick={clearCart}
            className="text-sm text-red-500 hover:text-red-600 font-medium"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Free shipping progress — prominent banner above items */}
            <div
              className={`rounded-2xl border p-4 sm:p-5 ${
                freeShippingReached
                  ? isDark
                    ? 'bg-emerald-900/20 border-emerald-700/40'
                    : 'bg-emerald-50 border-emerald-200'
                  : isDark
                    ? 'bg-gray-900 border-gray-800'
                    : 'bg-white border-gray-100'
              }`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <p
                  className={`text-sm font-medium flex items-center gap-2 ${
                    freeShippingReached
                      ? isDark
                        ? 'text-emerald-300'
                        : 'text-emerald-700'
                      : isDark
                        ? 'text-gray-200'
                        : 'text-gray-800'
                  }`}
                >
                  <Truck size={16} className="flex-shrink-0" />
                  {freeShippingReached ? (
                    <span>You&apos;ve unlocked <strong>FREE shipping</strong> 🎉</span>
                  ) : (
                    <span>
                      Add{' '}
                      <strong className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>
                        ₹{freeShippingRemaining.toLocaleString('en-IN')}
                      </strong>{' '}
                      more to unlock <strong>FREE shipping</strong>
                    </span>
                  )}
                </p>
                <span
                  className={`text-xs font-semibold flex-shrink-0 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {freeShippingPct}%
                </span>
              </div>
              <div
                className={`h-2 rounded-full overflow-hidden ${
                  isDark ? 'bg-gray-800' : 'bg-gray-100'
                }`}
              >
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500 ease-out"
                  style={{ width: `${freeShippingPct}%` }}
                />
              </div>
            </div>

            {state.items.map((item) => (
              <motion.div
                key={item.product.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border p-6 flex gap-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
              >
                <Link
                  to={`/product/${item.product.id}`}
                  className="w-24 h-24 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: item.product.colorAccent + '10' }}
                >
                  <img
                    src={item.product.image}
                    alt={`${item.product.name} nicotine pouches`}
                    className="w-20 h-20 object-contain"
                    loading="lazy"
                  />
                </Link>

                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product.id}`}>
                    <h3 className={`font-semibold hover:text-emerald-600 transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.product.name}
                    </h3>
                  </Link>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {item.product.strength}{item.product.strengthUnit} · {item.product.format} · {item.product.brand}
                  </p>

                  {(() => {
                    const stock = item.product.stockQuantity ?? 100;
                    const atMax = item.quantity >= stock;
                    return (
                    <>
                    {stock > 0 && stock < 10 && (
                      <p className="text-xs font-semibold text-red-500 mt-1 flex items-center gap-1">
                        <AlertTriangle size={12} /> Hurry, only {stock} left!
                      </p>
                    )}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200'}`}
                      >
                        <Minus size={14} />
                      </button>
                      <span className={`font-semibold w-6 text-center ${isDark ? 'text-white' : ''}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        disabled={atMax}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${atMax ? 'opacity-40 cursor-not-allowed' : ''} ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200'}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        ₹{(item.product.price * item.quantity).toLocaleString('en-IN')}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                    </>
                    );
                  })()}
                </div>
              </motion.div>
            ))}

          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className={`rounded-2xl border p-6 sticky top-24 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
              <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Order Summary</h3>

              {/* Coupon Section */}
              <div className="mb-6">
                {appliedCoupon ? (
                  <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'}`}>
                    <div className="flex items-center gap-2">
                      <Tag size={14} className="text-emerald-600" />
                      <span className={`text-sm font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{appliedCoupon.code}</span>
                      <span className={`text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
                        -₹{discountAmount.toLocaleString('en-IN')}
                      </span>
                    </div>
                    <button onClick={removeCoupon} className="text-gray-400 hover:text-red-500 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <button
                      onClick={() => setShowCoupons(!showCoupons)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        isDark ? 'bg-gray-800 border-gray-700 hover:border-emerald-600' : 'bg-gray-50 border-gray-200 hover:border-emerald-400'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Tag size={16} className="text-emerald-600" />
                        <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Apply Coupon</span>
                        {availableCoupons.length > 0 && (
                          <span className="text-xs bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-bold">
                            {availableCoupons.length}
                          </span>
                        )}
                      </div>
                      {showCoupons ? <ChevronUp size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} /> : <ChevronDown size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />}
                    </button>

                    {couponError && (
                      <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                        <AlertCircle size={12} /> {couponError}
                      </p>
                    )}

                    {showCoupons && (
                      <div className="mt-3 space-y-2">
                        {availableCoupons.length === 0 ? (
                          <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No coupons available</p>
                        ) : availableCoupons.map((c) => {
                          const isEligible = !c.min_order_value || totalPrice >= c.min_order_value;
                          const shortfall = c.min_order_value ? c.min_order_value - totalPrice : 0;
                          return (
                            <div
                              key={c.id}
                              className={`p-3 rounded-lg border transition-colors ${
                                isEligible
                                  ? `cursor-pointer ${isDark ? 'bg-gray-800 border-gray-700 hover:border-emerald-500' : 'bg-white border-gray-200 hover:border-emerald-400'}`
                                  : `${isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-gray-50 border-gray-100'} opacity-60`
                              }`}
                              onClick={() => isEligible && applyCoupon(c.code)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-bold tracking-wide px-2 py-0.5 rounded ${
                                    isDark ? 'bg-gray-700 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                                  }`}>{c.code}</span>
                                  <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                                  </span>
                                </div>
                                {isEligible ? (
                                  couponLoading === c.code
                                    ? <Loader2 size={14} className="animate-spin text-emerald-600" />
                                    : <span className={`text-xs font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>APPLY</span>
                                ) : null}
                              </div>
                              {c.description && (
                                <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{c.description}</p>
                              )}
                              {!isEligible && (
                                <p className="text-xs mt-1.5 text-amber-500 font-medium">
                                  Add ₹{shortfall.toLocaleString('en-IN')} more to use this coupon
                                  {c.min_order_value ? ` (min order ₹${c.min_order_value.toLocaleString('en-IN')})` : ''}
                                </p>
                              )}
                              {isEligible && c.max_discount && c.discount_type === 'percentage' && (
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Max discount ₹{c.max_discount.toLocaleString('en-IN')}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal ({totalItems} items)</span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{totalPrice.toLocaleString('en-IN')}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-600">Coupon Discount</span>
                    <span className="font-medium text-emerald-600">-₹{discountAmount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Shipping</span>
                  <span className={`font-medium ${shipping === 0 ? 'text-emerald-600' : isDark ? 'text-white' : 'text-gray-900'}`}>
                    {shipping === 0 ? 'FREE' : `₹${shipping}`}
                  </span>
                </div>
                <div className={`border-t pt-3 flex justify-between ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
                  <span className={`text-xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{grandTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div
                className={`p-3 rounded-lg mb-4 ${
                  freeShippingReached
                    ? isDark
                      ? 'bg-emerald-900/30'
                      : 'bg-emerald-50'
                    : isDark
                      ? 'bg-amber-900/30'
                      : 'bg-amber-50'
                }`}
              >
                <div
                  className={`text-xs flex items-center justify-between gap-2 mb-2 ${
                    freeShippingReached
                      ? isDark
                        ? 'text-emerald-400'
                        : 'text-emerald-700'
                      : isDark
                        ? 'text-amber-400'
                        : 'text-amber-700'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Truck size={14} />
                    {freeShippingReached
                      ? 'You qualify for free shipping!'
                      : `Add ₹${freeShippingRemaining.toLocaleString('en-IN')} more for free shipping`}
                  </span>
                  <span className="font-semibold">{freeShippingPct}%</span>
                </div>
                <div
                  className={`h-1.5 rounded-full overflow-hidden ${
                    isDark ? 'bg-gray-800' : 'bg-white/70'
                  }`}
                >
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500 ease-out"
                    style={{ width: `${freeShippingPct}%` }}
                  />
                </div>
              </div>

              <Link
                to="/checkout"
                className="block w-full py-4 bg-emerald-600 text-white text-center rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
              >
                Proceed to Checkout <ArrowRight size={16} className="inline ml-1" />
              </Link>

              <p className={`text-xs text-center mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Taxes calculated at checkout. Secure, encrypted payment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
